import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PlayCircle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * HowToUseSection - Standardized "How to Use" component with YouTube tutorial placeholder
 * 
 * This component provides a consistent design across all modules for:
 * - How to Use sections
 * - YouTube tutorial link blocks
 * - Collapsible/expandable behavior
 * 
 * @param {string} featureName - Name of the feature (e.g., "Snapshot Analysis")
 * @param {string} youtubeUrl - YouTube video URL (use 'YOUTUBE_URL_PLACEHOLDER' for placeholder)
 * @param {boolean} defaultOpen - Whether the section is expanded by default (default: true)
 */
export default function HowToUseSection({ 
  featureName = 'this feature',
  youtubeUrl = 'YOUTUBE_URL_PLACEHOLDER',
  defaultOpen = true
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isPlaceholder = !youtubeUrl || youtubeUrl === 'YOUTUBE_URL_PLACEHOLDER';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button 
          className="w-full flex items-center justify-between p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          data-testid="howto-toggle"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <PlayCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <span className="font-semibold text-red-700 text-sm sm:text-base">How to Use This Feature</span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-red-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-red-600" />
          )}
        </button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-3">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-5">
          <div className="flex items-center gap-3 mb-3">
            <PlayCircle className="h-5 w-5 text-red-600" />
            <p className="text-xs sm:text-sm font-medium text-red-800">Watch tutorial video here:</p>
          </div>
          
          {isPlaceholder ? (
            /* YouTube Placeholder */
            <div 
              className="bg-white border-2 border-dashed border-red-300 rounded-lg p-6 sm:p-8 text-center"
              data-testid="youtube-placeholder"
            >
              <PlayCircle className="h-10 w-10 sm:h-12 sm:w-12 text-red-300 mx-auto mb-3" />
              <p className="text-xs sm:text-sm text-gray-500 mb-2">YouTube video coming soon</p>
              <p className="text-xs text-gray-400">[YouTube link to be added]</p>
            </div>
          ) : (
            /* YouTube Link */
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 sm:p-5 bg-white border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors group"
              data-testid="youtube-link"
            >
              <div className="p-3 bg-red-100 rounded-full group-hover:bg-red-200 transition-colors">
                <PlayCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-red-800 group-hover:text-red-900">
                  Watch: How to use {featureName}
                </p>
                <p className="text-xs sm:text-sm text-red-600 group-hover:text-red-700">
                  Opens in YouTube
                </p>
              </div>
            </a>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
